// frontend/src/lib/transition.tsx
// Global theatrical transition layer. Lives in App so it survives route
// changes, Landing navigates mid-animation, and the iris overlay keeps
// closing over the freshly-mounted next page.
//
// The iris is rAF-driven (not CSS transition) so its center can track
// the cursor in real time while the circle is shrinking, as long as the
// cursor stays inside the current circle.
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { prefersReducedMotion } from './motion';
import { Z } from './zIndex';

// Exported so tests can advance fake timers by the real duration instead of
// hardcoding a copy of it that could quietly drift from this one.
export const IRIS_MS = 700;
// Fast-out easing: the circle collapses hard in the first frames and
// settles gently, reads as responsive where the old 1900ms ease-in-out
// read as sluggish.
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Locks page scroll for the duration of the theatrical transition (curtain
 * + iris). Without this, a swipe mid-transition on a page taller than the
 * viewport (Landing) genuinely scrolls the DOCUMENT: the old page is still
 * mounted, so the scroll is real, not swallowed. Then navigate() swaps the
 * document out from under that scroll offset, the new route mounts at
 * whatever scrollY the swipe left behind (or the browser clamps it), and the
 * net effect reads as "my swipe did nothing and the page snapped back on its
 * own." A curtain coming down is exactly the moment the page should not be
 * moving, so this makes that true up front instead of discarding the motion
 * after the fact.
 *
 * `active` is `cursorOn || irisActive` (see TransitionProvider below), which
 * is already the one boolean that spans every launch path through this
 * provider: the curtain+iris path (cursorOn flips on at click, both flip off
 * together when the iris finishes) and the bare-iris footer links
 * (irisActive only). It is also the same boolean startIris's reduced-motion
 * branch zeroes out immediately, so reduced motion shortens this lock to
 * effectively nothing for free, satisfying requirement 3 without a second
 * reduced-motion check living here.
 *
 * Leak-proofing: this is a plain effect keyed on `active`. React guarantees
 * the returned cleanup runs before the effect re-fires (so the instant
 * `active` goes false, the lock is undone before the new, no-op effect body
 * even runs) AND unconditionally on unmount, mid-transition unmount, a
 * navigate() that throws, or the user hitting Back included. There is no
 * second "unlock" call anywhere else to forget, so there is nothing to leak:
 * the lock and its release are the same closure.
 */
function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const { style } = document.body;
    const rootStyle = document.documentElement.style;
    const prevOverflow = style.overflow;
    const prevRootOverflow = rootStyle.overflow;
    const prevPaddingRight = style.paddingRight;

    // Measured, not assumed: a scrollbar can be 0px (touch/overlay
    // scrollbars, macOS "when scrolling"), or a good 15-17px on a classic
    // desktop scrollbar. innerWidth includes whatever scrollbar is actually
    // on screen right now; the root element's clientWidth never does, so
    // the gap between the two IS the scrollbar's real width in this browser,
    // on this display, today.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    style.overflow = 'hidden';
    // The html element as well as the body, because this fix exists for
    // phones and overflow:hidden on the body alone does not stop iOS Safari:
    // there it is the root element that scrolls, so a body-only lock is a
    // lock that works everywhere except the browser the defect was reported
    // on.
    rootStyle.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      // Compensate so removing the scrollbar doesn't widen the viewport and
      // shove everything sideways. Added to whatever padding-right the page
      // already had, not overwritten, and the previous value (padding AND
      // overflow) is restored verbatim on cleanup, never assumed to be '0'.
      const prevPadRightPx = parseFloat(prevPaddingRight || '0') || 0;
      style.paddingRight = `${prevPadRightPx + scrollbarWidth}px`;
    }

    return () => {
      style.overflow = prevOverflow;
      rootStyle.overflow = prevRootOverflow;
      style.paddingRight = prevPaddingRight;
    };
  }, [active]);
}

export const VELVET_BG = `
  repeating-linear-gradient(90deg,
    rgba(0,0,0,0)       0px,
    rgba(0,0,0,.65)    14px,
    rgba(0,0,0,0)      28px,
    rgba(70,10,10,.30) 44px,
    rgba(0,0,0,.72)    62px,
    rgba(0,0,0,0)      90px),
  linear-gradient(180deg, #2a0404 0%, #3d0707 22%, #2d0505 55%, #0f0101 100%)
`;

interface TransitionCtx {
  beginLaunch: () => void;
  startIris: (cx: number, cy: number, navigateTo: string) => void;
}
const Ctx = createContext<TransitionCtx | null>(null);

export function useTransition(): TransitionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTransition must be inside TransitionProvider');
  return ctx;
}

export function TransitionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [cursorOn, setCursorOn] = useState(false);
  const [irisActive, setIrisActive] = useState(false);
  const irisRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const cleanupRef = useRef<number | null>(null);

  // Shared global mouse tracking, used by both cursor and iris.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
      const el = cursorRef.current;
      if (el) el.style.transform = `translate(${e.clientX - 24}px, ${e.clientY - 24}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const beginLaunch = useCallback(() => {
    setCursorOn(true);
  }, []);

  const startIris = useCallback((cx0: number, cy0: number, navigateTo: string) => {
    // Navigate first so the next page paints under the overlay.
    navigate(navigateTo);

    // Deliberate, not accidental: a client-side route swap does not reset
    // window scroll by itself, so whatever scrollY the OLD page (or a swipe
    // during the curtain) left behind would otherwise carry straight into
    // the new page, landing it pre-scrolled to a spot that means nothing on
    // the new layout. The scroll lock below keeps this pinned at 0 for the
    // rest of the transition; this is what puts it at 0 in the first place.
    window.scrollTo(0, 0);

    // The circle radius is written to clipPath every rAF frame, direct style
    // writes the CSS reduced-motion guard cannot see. Checking here (not at
    // each call site) keeps every caller safe by construction, and it is
    // also the only place cursorOn ever flips back to false, so a caller
    // that already called beginLaunch() doesn't strand the gavel cursor on.
    if (prefersReducedMotion()) {
      setIrisActive(false);
      setCursorOn(false);
      return;
    }

    setIrisActive(true);

    let startTime = performance.now();
    // Click during the iris = skip: fast-forward to the end this frame.
    const skip = () => { startTime = Number.NEGATIVE_INFINITY; };
    window.addEventListener('pointerdown', skip);
    // Initial center comes from the caller; we may follow the cursor
    // during the animation as long as it's inside the current circle.
    let curCx = cx0;
    let curCy = cy0;
    const maxR = 2 * Math.max(window.innerWidth, window.innerHeight);

    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / IRIS_MS);
      const progress = easeOutCubic(t);
      const r = maxR * (1 - progress);

      // If the mouse is inside the current circle, lerp center toward it.
      const { x: mx, y: my } = mousePosRef.current;
      const dx = mx - curCx;
      const dy = my - curCy;
      const mouseDist = Math.hypot(dx, dy);
      if (mouseDist < r - 8) {
        // Leave a small margin so the center doesn't hit the edge.
        curCx += dx * 0.12;
        curCy += dy * 0.12;
      }

      const el = irisRef.current;
      if (el) {
        const clip = `circle(${r}px at ${curCx}px ${curCy}px)`;
        el.style.clipPath = clip;
        // @ts-ignore
        el.style.webkitClipPath = clip;
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    // First frame, paint at full radius before transitioning.
    rafRef.current = requestAnimationFrame(tick);

    if (cleanupRef.current) window.clearTimeout(cleanupRef.current);
    cleanupRef.current = window.setTimeout(() => {
      window.removeEventListener('pointerdown', skip);
      setIrisActive(false);
      setCursorOn(false);
    }, IRIS_MS + 80);
  }, [navigate]);

  // Hide native cursor while gavel is on.
  useEffect(() => {
    if (!cursorOn) return;
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'none';
    return () => { document.body.style.cursor = prev; };
  }, [cursorOn]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (cleanupRef.current) window.clearTimeout(cleanupRef.current);
  }, []);

  // One boolean, "is the theatrical transition running right now", shared by
  // the overlay portal below and the scroll lock. See useScrollLock's own
  // comment for why this single flag is enough to cover every launch path
  // and to collapse correctly under prefers-reduced-motion.
  const transitioning = irisActive || cursorOn;
  useScrollLock(transitioning);

  const overlays = transitioning && typeof document !== 'undefined'
    ? createPortal(
        <>
          {irisActive && <IrisOverlay innerRef={irisRef} />}
          {cursorOn && <GavelCursor innerRef={cursorRef} />}
        </>,
        document.body,
      )
    : null;

  return (
    <Ctx.Provider value={{ beginLaunch, startIris }}>
      {children}
      {overlays}
    </Ctx.Provider>
  );
}

function IrisOverlay({ innerRef }: { innerRef: React.RefObject<HTMLDivElement> }) {
  // clipPath is written imperatively in the rAF tick. Initial clipPath
  // is full viewport so the first paint covers the whole page.
  return (
    <div
      ref={innerRef}
      aria-hidden
      style={{
        // Z.overlay, not the header's rung: this covers the whole viewport
        // and now runs on routes that HAVE a header. At an equal z-index the
        // two were separated only by the portal landing later in the body.
        position: 'fixed', inset: 0, zIndex: Z.overlay, pointerEvents: 'none',
        backgroundColor: '#1c0404',
        backgroundImage: `url("/images/bench-nobg.webp"), ${VELVET_BG}`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundPosition: 'center 85%, top left',
        backgroundSize: 'contain, auto',
        filter: 'brightness(.55) saturate(.9)',
        clipPath: 'circle(200vmax at 50% 50%)',
        WebkitClipPath: 'circle(200vmax at 50% 50%)',
        willChange: 'clip-path',
      }}
    />
  );
}

function GavelCursor({ innerRef }: { innerRef: React.RefObject<HTMLDivElement> }) {
  return (
    <div
      ref={innerRef}
      aria-hidden
      className="gavel-cursor"
      style={{ width: 48, height: 48, willChange: 'transform' }}
    >
      <svg viewBox="0 0 48 48" width={48} height={48}>
        {/* Stand */}
        <rect x="10" y="36" width="28" height="5" rx="1.2" fill="#1a0f08" />
        <rect x="14" y="32" width="20" height="5" rx="1.2" fill="#2a1810" />
        {/* Gavel swings above the stand */}
        <g className="swing">
          <rect x="14" y="18" width="22" height="5" rx="2.5" fill="#6b3a1e" />
          <rect x="10" y="10" width="9" height="22" rx="2" fill="#5b3a1e" />
          <rect x="10" y="12" width="9" height="1.6" fill="#b8892f" />
          <rect x="10" y="28" width="9" height="1.6" fill="#b8892f" />
        </g>
      </svg>
    </div>
  );
}
